import React, {useState, useEffect, useRef} from 'react'
import Head from 'next/head'

import {HStack, VStack} from '@components/Stack'
import {useUser} from 'src/hooks/useUser'
import {Panel} from '@components/Panel'
import {Button} from 'src/Button'
import {Icon} from 'src/Icon'
import {motion} from 'framer-motion'
import {cdnifyStorageUrl, showConfetti} from 'src/utils'
import {TypewriterMessage} from '@components/TypewriterMessage'
import {BubbleLoaderWithText} from '@components/BubbleLoader'
import {API, fetchLevel} from 'src/util/db'
import {AvatarImage} from 'src/Common'

const Stats = () => {
  // Page title effect
  useEffect(() => {
    window.document.title = '2024 Year Wrapped - CSSBattle'
  }, [])
  const user = useUser()

  const [data, setData] = useState()

  useEffect(() => {
    if (!user?.uid) return

    fetch(`${API}/yearWrapped?userId=${user.uid}`)
      .then((res) => res.json())
      .then(async (_data) => {
        if (_data?.totalSubmissions !== undefined) {
          const joinedYear = new Date(
            _data.profile.joinedOn._seconds * 1000
          ).getFullYear()
          _data.joinedYear = joinedYear
          _data.yearCount = `${
            ['', '2nd', '3rd', '4th', '5th', '6th'][2024 - joinedYear]
          }`

          if (_data?.totalSubmissions === 0) {
            setData(_data)
            return
          }
          const highestMonthSubmissionCount = Math.max(
            ...Object.values(_data.monthSubmissionCounts)
          )
          _data.highestMonthSubmissionCount = highestMonthSubmissionCount

          // fetch daily targets
          const levels = await Promise.all(
            _data.mostPlayedTargets
              .filter((t) => t.levelId.length > 5)
              .map((t) => fetchLevel(t.levelId))
          )
          if (levels.length) {
            _data.levels = {}
            levels.forEach((level) => {
              _data.levels[level.id] = level
            })
          }
          showConfetti(3)

          window.data = _data

          setData(_data)
        }
      })
  }, [user?.uid])

  useEffect(() => {
    const id = setInterval(() => {
      // getGlobalStats().then((stats) => {
      //   setStats(stats)
      // })
    }, 2000)

    return () => {
      clearInterval(id)
    }
  }, [])

  const [isDownloading, setIsDownloading] = useState(false)
  return (
    <>
      <Head>
        <title key="title">2024 Year Wrapped | CSSBattle</title>
        <meta
          property="og:title"
          content="2024 Year Wrapped | CSSBattle"
          key="ogTitle"
        />

        <script src="https://html2canvas.hertzen.com/dist/html2canvas.min.js"></script>
      </Head>

      {/* <InnerPageHeader icon="stats" title="Stats" /> */}

      {data ? (
        <div className="year-wrapped-container">
          <VStack align="center" gap="4rem">
            <div className="yw-content" id="yw-content">
              <VStack align="center" gap="2rem">
                <Unit panel={false}>
                  <VStack gap="0">
                    <img
                      src="/images/logo-new.svg"
                      alt="CSSBattle"
                      height="30"
                    />
                    <div
                      className="yw-highlight"
                      style={{
                        fontSize: '1rem',
                        letterSpacing: '4px',
                        position: 'relative',
                        left: '2.5px',
                        fontWeight: '400',
                        textTransform: 'uppercase',
                      }}
                    >
                      2024 Year Wrapped
                    </div>
                  </VStack>
                </Unit>

                <Unit panel={false}>
                  <VStack gap="0">
                    <AvatarImage
                      url={data.profile.avatar}
                      size={'64'}
                      useCdnUrl
                    />
                    <div>
                      Hey{' '}
                      <strong>
                        {data.profile?.username
                          ? `@${data.profile.username}`
                          : data.profile?.displayName}
                      </strong>{' '}
                      👋🏼
                    </div>
                  </VStack>
                </Unit>

                {data.joinedYear === 2024 ? (
                  <Unit>This was your first year at CSSBattle! ♥️</Unit>
                ) : (
                  <Unit>
                    This was your {data.yearCount} year at CSSBattle!{' '}
                    {new Array(2024 - data.joinedYear + 1).fill('🎉').join('')}
                  </Unit>
                )}

                {data.totalSubmissions ? (
                  <>
                    <Unit>
                      You played a total of{' '}
                      <span className="yw-highlight">
                        {data.totalTargetsPlayed}
                      </span>{' '}
                      targets this year!
                      <HStack gap="1rem" justify="center">
                        <div className="yw-text--big yw-number yw-highlight">
                          {data.battleTargets}
                        </div>
                        <div className="yw-text--small">battle targets</div>
                        <div className="yw-text--big yw-number yw-highlight">
                          {data.dailyTargets}
                        </div>
                        <div className="yw-text--small">daily targets</div>
                      </HStack>
                    </Unit>

                    <Unit>
                      Guess how many code submissions you made in 2024?
                      <HStack gap="1rem" justify="center" align="center">
                        <div className="yw-text--big yw-number yw-highlight">
                          {data.totalSubmissions}
                        </div>
                        <div className="yw-text--small">submissions! 🤯</div>
                      </HStack>
                    </Unit>

                    <Unit>
                      ...and that is nearly
                      <HStack gap="1rem" justify="center" align="center">
                        <div className="yw-text--big yw-number yw-highlight">
                          {Math.round((data.totalSubmissions * 6) / 60)}
                        </div>
                        <div className="yw-text--small">
                          minutes of network time in submissions
                        </div>
                      </HStack>
                      <HStack gap="1rem" justify="center" align="center">
                        <div className="yw-text--big yw-number yw-highlight">
                          {Math.round(data.avgSubmissionsPerTarget)}
                        </div>
                        <div className="yw-text--small">
                          submissions per target on average!
                        </div>
                      </HStack>
                    </Unit>

                    <Unit>
                      <VStack gap="1rem">
                        ...and you know which target you played the most?
                        <HStack
                          gap="1rem"
                          justify="center"
                          align="flex-end"
                          responsive
                        >
                          <Target
                            img={
                              data.mostPlayedTargets[1].levelId.length > 5
                                ? data.levels[data.mostPlayedTargets[1].levelId]
                                    .image
                                : `/targets/${data.mostPlayedTargets[1].levelId}.png`
                            }
                            rank="2"
                            count={data.mostPlayedTargets[1].count}
                          />
                          <Target
                            img={
                              data.mostPlayedTargets[0].levelId.length > 5
                                ? data.levels[data.mostPlayedTargets[0].levelId]
                                    .image
                                : `/targets/${data.mostPlayedTargets[0].levelId}.png`
                            }
                            rank="1"
                            count={data.mostPlayedTargets[0].count}
                          />
                          <Target
                            img={
                              data.mostPlayedTargets[2].levelId.length > 5
                                ? data.levels[data.mostPlayedTargets[2].levelId]
                                    .image
                                : `/targets/${data.mostPlayedTargets[2].levelId}.png`
                            }
                            rank="3"
                            count={data.mostPlayedTargets[2].count}
                          />
                        </HStack>
                        <div className="yw-text--small">
                          You made{' '}
                          <span className="yw-highlight">
                            {data.mostPlayedTargets[0].count}
                          </span>{' '}
                          submissions on top target!
                        </div>
                      </VStack>
                    </Unit>

                    <Unit>
                      Shortest code you solved a target in was just
                      <HStack gap="1rem" justify="center">
                        <div className="yw-text--big yw-number yw-highlight">
                          {data.bestCodeSize}
                        </div>
                        <div className="yw-text--small">characters!</div>
                      </HStack>
                    </Unit>

                    <Unit>
                      Fun fact: Do you know how many times you solved a target
                      in first attempt?
                      <HStack gap="1rem" justify="center">
                        <div className="yw-text--big yw-number yw-highlight">
                          {data.perfectFirstAttempts}
                        </div>
                        <div className="yw-text--small">times!!</div>
                      </HStack>
                    </Unit>

                    {data.progress.firstMonthAvg ? (
                      <Unit>
                        An year is all about progress. You went all the way from
                        <HStack gap="1rem" justify="center" responsive>
                          <VStack gap="0">
                            <div className="yw-text--big yw-number yw-highlight">
                              {Math.round(data.progress.firstMonthAvg)}
                            </div>
                            <div
                              className="yw-text--small"
                              style={{marginTop: '-1.5rem'}}
                            >
                              characters
                            </div>
                          </VStack>
                          <div className="yw-text--small">
                            {' '}
                            on average in {data.progress.firstMonthName}
                          </div>
                          <div className="yw-text--small">to</div>

                          <VStack gap="0">
                            <div className="yw-text--big yw-number yw-highlight">
                              {Math.round(data.progress.lastMonthAvg)}
                            </div>
                            <div
                              className="yw-text--small"
                              style={{marginTop: '-1.5rem'}}
                            >
                              characters
                            </div>
                          </VStack>
                          <div className="yw-text--small">
                            {' '}
                            in {data.progress.lastMonthName}
                          </div>
                        </HStack>
                      </Unit>
                    ) : null}

                    <Unit panel={false}>
                      Let's talk about your consistency in 2024! Shall we?
                    </Unit>

                    <Unit>
                      <VStack gap="1rem">
                        Your monthly submissions
                        <div className="yw-month-chart">
                          {new Array(12).fill(0).map((_, index) => (
                            <motion.div
                              key={index}
                              className="yw-month-chart-bar"
                              style={{
                                height: `${
                                  ((data.monthSubmissionCounts[index] || 0) /
                                    data.highestMonthSubmissionCount) *
                                  100
                                }%`,
                              }}
                              whileHover={{scale: 1.1}}
                            >
                              <span className="yw-month-chart-bar-text">
                                {data.monthSubmissionCounts[index] || 0}
                              </span>
                            </motion.div>
                          ))}
                        </div>
                      </VStack>
                    </Unit>

                    <Unit>
                      You managed to get a longest streak of
                      <HStack gap="1rem" justify="center">
                        <div className="yw-text--big yw-number yw-highlight">
                          {data.streak.longestStreak}
                        </div>
                        <div className="yw-text--small">days!</div>
                      </HStack>
                    </Unit>

                    <Unit>
                      You played the most in
                      <div className="yw-text--big yw-number yw-highlight">
                        {data.mostPlayedMonthName}
                      </div>
                    </Unit>

                    <Unit>
                      ...and seems like your favourite day to play was
                      <div className="yw-text--big yw-number yw-highlight">
                        {data.mostPlayedDayName}
                      </div>
                    </Unit>

                    <Unit panel={false}>
                      <p
                        style={{maxWidth: '50ch', lineHeight: 1.5}}
                        className="newFont"
                      >
                        We hope you enjoyed your time at CSSBattle in 2024! And
                        we look forward to see you in 2025, having more fun than
                        ever!
                      </p>
                    </Unit>
                  </>
                ) : (
                  <Unit>
                    You didn't play any targets in 2024. We hope you'll join us
                    in 2025!
                  </Unit>
                )}

                <Unit panel={false}>
                  <div className="yw-text--big yw-number yw-highlight">
                    Happy 2025! 🎉
                  </div>
                </Unit>
              </VStack>
            </div>

            {data.totalSubmissions ? (
              <Button
                isLoading={isDownloading}
                onClick={() => {
                  setIsDownloading(true)
                  html2canvas(document.querySelector('#yw-content'), {
                    backgroundColor: '#07080b',
                    useCORS: true,
                  })
                    .then((canvas) => {
                      canvas.toBlob((blob) => {
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = 'CSSBattle-2024-year-wrapped.png'
                        a.click()
                      })
                    })
                    .finally(() => {
                      setIsDownloading(false)
                    })
                }}
              >
                <HStack gap="0.5rem">
                  <Icon name="download" />
                  Download & Share your 2024 wrapped
                </HStack>
              </Button>
            ) : null}
          </VStack>
        </div>
      ) : (
        <TypewriterMessage message="">
          <motion.p
            style={{margin: '1rem 0'}}
            className="ta-c"
            initial={{opacity: 0}}
            exit={{opacity: 0}}
            animate={{opacity: 1}}
          >
            <BubbleLoaderWithText
              width={300}
              bgColor="var(--clr-bg)"
              startLevel={72}
            >
              Remembering your 2024...
            </BubbleLoaderWithText>
          </motion.p>
        </TypewriterMessage>
      )}
    </>
  )
}

const Unit = ({panel = true, children}) => {
  // return panel ? (
  //   <Panel className="yw-unit">{children}</Panel>
  // ) : (
  return (
    <motion.div
      className="yw-unit"
      data-bg={panel}
      initial={{opacity: 0, y: 100}}
      animate={{opacity: 1, y: 0}}
      transition={{duration: 0.5}}
    >
      {children}
    </motion.div>
  )
}

const Target = ({img, rank, count}) => {
  const src = cdnifyStorageUrl(img)
  return (
    <div className="yw-target-wrap" data-rank={rank}>
      <div className="yw-target__count">{rank}</div>
      <img className="yw-target-img" src={src} alt="Target" />
    </div>
  )
}

export default Stats
